import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Filter, Zap, Search, CheckCircle2, Loader2, Building2, CalendarDays } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import type { Candidate } from "@/types/candidate";

interface ScoredJob {
	jobId: string;
	jobTitle: string;
	jobStatus: string | null;
	combinedScore: number;
	isAlreadyMatched: boolean;
	matchReasons: string[];
	skillComparison: Array<{
		candidateSkillId: string;
		candidateSkillName: string;
		jobSkillId: string | null;
		jobSkillName: string | null;
		similarity: number;
		matched: boolean;
	}>;
	experienceComparison: Array<{
		candidateExperienceId: string;
		candidateExperienceName: string;
		jobExperienceId: string | null;
		jobExperienceName: string | null;
		similarity: number;
		matched: boolean;
	}>;
}

interface CandidatePotentialMatchesTabProps {
	candidateId: string;
	candidate: Candidate;
}

interface JobMetaInfo {
	status: string | null;
	companyName: string | null;
	applicationDeadline: string | null;
}

export function CandidatePotentialMatchesTab({ candidateId, candidate }: CandidatePotentialMatchesTabProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const progressEndRef = useRef<HTMLDivElement>(null);

	const [isSearching, setIsSearching] = useState(false);
	const [jobSummaries, setJobSummaries] = useState<Record<string, string>>({});
	const [jobMetaById, setJobMetaById] = useState<Record<string, JobMetaInfo>>({});
	const [hasSearched, setHasSearched] = useState(false);
	const [searchResults, setSearchResults] = useState<{ shortlist: ScoredJob[] } | null>(null);
	const [searchProgress, setSearchProgress] = useState<Array<{
		step: string;
		status: 'pending' | 'running' | 'completed' | 'error';
		data?: any;
	}>>([]);
	const [creatingMatchFor, setCreatingMatchFor] = useState<string | null>(null);
	const [flexibilityLevel, setFlexibilityLevel] = useState<'strict' | 'normal' | 'flexible'>('normal');

	const statusBadgeClass = (status?: string | null) => {
		switch ((status || '').toLowerCase()) {
			case 'open':
				return 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800';
			case 'hold':
				return 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-800';
			case 'closed/won':
				return 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-800';
			case 'closed/lost':
				return 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-800';
			default:
				return 'bg-muted text-muted-foreground border-border';
		}
	};

	const formatDeadline = (deadline?: string | null) => {
		if (!deadline)
			return null;

		const date = new Date(deadline);
		if (Number.isNaN(date.getTime()))
			return null;

		return date.toLocaleDateString();
	};

	// Set up realtime channel
	useEffect(() => {
		let channel: any = null;

		const setupChannel = async () => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) return;

			const channelId = `candidate_search:${candidateId}:${user.id}`;
			channel = supabase.channel(channelId);

			channel
				.on('broadcast', { event: 'search_progress' }, (payload: any) => {
					const event = payload.payload;

					if (event.step === 'complete') {
					if (event.result?.shortlist) {
						// Align with AdvancedSearchPanel: flexibility-based threshold
						const scoreThreshold = flexibilityLevel === 'strict' ? 30 : flexibilityLevel === 'flexible' ? 10 : 20;
						const filtered = event.result.shortlist.filter((j: any) => j.combinedScore >= scoreThreshold);
						event.result.shortlist = filtered;
						if (filtered.length > 0) {
							const ids = filtered.map((j: any) => j.jobId);
							supabase.from('jobs').select('id, ai_summary').in('id', ids).then(({ data }) => {
								if (data) {
									const map: Record<string, string> = {};
									for (const row of data)
										if (row.ai_summary) map[row.id] = row.ai_summary;
									setJobSummaries(map);
								}
							});
						}
					}
						setSearchResults(event.result);
						setIsSearching(false);
					} else if (event.step === 'error') {
						toast({
							title: t("errors.error"),
							description: event.message || t("candidateJobSearch.toasts.searchError"),
							variant: "destructive",
						});
						setIsSearching(false);
					} else {
						setSearchProgress(prev => {
							const existing = prev.find(p => p.step === event.step);
							if (existing)
								return prev.map(p => p.step === event.step ? event : p);
							return [...prev, event];
						});
					}
				})
				.subscribe();
		};

		setupChannel();

		return () => {
			if (channel)
				supabase.removeChannel(channel);
		};
	}, [candidateId, t, flexibilityLevel]);

	// Auto-scroll progress
	useEffect(() => {
		if (searchProgress.length > 0)
			progressEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [searchProgress]);

	const runSearch = () => {
		setIsSearching(true);
		setHasSearched(true);
		setSearchResults(null);
		setSearchProgress([]);
		setJobSummaries({});
		setJobMetaById({});

		supabase.functions.invoke(
			`digger-intelligence?command=match-candidate-with-jobs-fast&candidateId=${candidateId}&flexibilityLevel=${flexibilityLevel}`,
			{ method: 'GET' }
		);
	};

	useEffect(() => {
		const hydrateJobMeta = async () => {
			if (!searchResults?.shortlist?.length) {
				setJobMetaById({});
				return;
			}

			const ids = searchResults.shortlist.map((job) => job.jobId);
			const { data } = await supabase
				.from('jobs')
				.select('id, status, application_deadline, company:company_id(name)')
				.in('id', ids);

			if (!data)
				return;

			const map: Record<string, JobMetaInfo> = {};
			for (const row of data as Array<any>) {
				map[row.id] = {
					status: row.status ?? null,
					companyName: row.company?.name ?? null,
					applicationDeadline: row.application_deadline ?? null,
				};
			}

			setJobMetaById(map);
		};

		hydrateJobMeta();
	}, [searchResults]);

	// Auto-run on mount and whenever flexibility changes
	useEffect(() => {
		runSearch();
	}, [candidateId, flexibilityLevel]);

	const handleCreateMatch = async (job: ScoredJob) => {
		setCreatingMatchFor(job.jobId);
		try {
			const { data: existing } = await supabase
				.from('matches')
				.select('id')
				.eq('job_id', job.jobId)
				.eq('candidate_id', candidateId)
				.maybeSingle();

			if (existing) {
				toast({
					title: t("candidateJobSearch.toasts.alreadyMatched"),
				});
				setSearchResults(prev => {
					if (!prev) return prev;
					return {
						...prev,
						shortlist: prev.shortlist.map(j =>
							j.jobId === job.jobId ? { ...j, isAlreadyMatched: true } : j
						)
					};
				});
				return;
			}

			const { data, error } = await supabase
				.from('matches')
				.insert([{ job_id: job.jobId, candidate_id: candidateId, score: 0, status: 'matched' }])
				.select('id');

			if (error) throw error;

			for (const match of data) {
				await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
					method: 'POST',
					body: {
						entityId: match.id,
						entityType: "match",
						analysisType: "full-match",
						actionOnComplete: "update-match",
					}
				});
			}

			toast({
				title: t("advancedSearch.toasts.success"),
				description: t("candidateJobSearch.toasts.matchCreated", { jobTitle: job.jobTitle }),
			});

			setSearchResults(prev => {
				if (!prev) return prev;
				return {
					...prev,
					shortlist: prev.shortlist.map(j =>
						j.jobId === job.jobId ? { ...j, isAlreadyMatched: true } : j
					)
				};
			});
		} catch (_error) {
			toast({
				title: t("errors.error"),
				description: t("candidateJobSearch.toasts.matchError"),
				variant: "destructive",
			});
		} finally {
			setCreatingMatchFor(null);
		}
	};



	// Loading state
	if (isSearching || (!searchResults && hasSearched)) {
		return (
			<div className="flex flex-col items-center justify-center py-16">
				<div className="relative mb-8">
					<div className="absolute inset-0 -m-4 rounded-full border-2 border-blue-300/40 dark:border-blue-600/30 animate-ping" style={{ animationDuration: '3s' }} />
					<div className="absolute inset-0 -m-2 rounded-full border border-blue-200/50 dark:border-blue-700/30 animate-pulse" style={{ animationDuration: '2s' }} />
					<div className="h-24 w-24 rounded-full bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center shadow-lg shadow-blue-200/50 dark:shadow-blue-900/30">
						<Brain className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-pulse" />
					</div>
					<div
						className="absolute h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center shadow-md"
						style={{ top: '-8px', right: '-12px', animation: 'bounce 2.5s ease-in-out infinite' }}
					>
						<Filter className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
					</div>
					<div
						className="absolute h-9 w-9 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center shadow-md"
						style={{ bottom: '-4px', left: '-14px', animation: 'bounce 2s ease-in-out infinite 0.5s' }}
					>
						<Zap className="h-5 w-5 text-amber-500 dark:text-amber-400" />
					</div>
				</div>

				<p className="text-base font-semibold text-foreground mb-1">{t("candidateJobSearch.progress.analyzing")}</p>
				<p className="text-xs text-muted-foreground mb-6">{t("advancedSearch.progress.patience")}</p>

				{searchProgress.length > 0 && (
					<div className="w-full max-w-md space-y-1.5">
						{searchProgress.filter(p => p.status === 'completed').map(progress => (
							<div key={progress.step} className="flex items-start gap-2 text-xs text-muted-foreground">
								<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
								<div className="flex-1 min-w-0">
									<span className="font-medium text-foreground">
										{t(`advancedSearch.progress.steps.${progress.step}`, { defaultValue: progress.step })}
									</span>
									{progress.data && (
										<span className="text-muted-foreground ml-1">
											({Object.entries(progress.data).map(([k, v]) =>
												`${t(`advancedSearch.progress.metrics.${k}`, { defaultValue: k })}: ${v}`
											).join(' \u2022 ')})
										</span>
									)}
								</div>
							</div>
						))}
						<div ref={progressEndRef} />
					</div>
				)}
			</div>
		);
	}

	// No results state
	if (!searchResults || !searchResults.shortlist || searchResults.shortlist.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-4">
				<p>{t("candidateJobSearch.noResults")}</p>
				<div className="flex items-center gap-1.5">
					<span className="text-xs text-muted-foreground mr-0.5">{t("advancedSearch.flexibility.label")}:</span>
					<div className="flex items-center gap-0.5 border border-border rounded p-0.5">
						{(['strict', 'normal', 'flexible'] as const).map(level => (
							<button
								key={level}
								onClick={() => setFlexibilityLevel(level)}
								className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
									flexibilityLevel === level
										? 'bg-blue-500 text-white'
										: 'text-muted-foreground hover:text-foreground'
								}`}
							>
								{t(`advancedSearch.flexibility.${level}`)}
							</button>
						))}
					</div>
				</div>
				<Button variant="outline" onClick={runSearch}>
					<Search className="h-4 w-4 mr-2" />
					{t("candidateJobSearch.actions.retry")}
				</Button>
			</div>
		);
	}

	// Results
	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between gap-3 flex-wrap">
				<p className="text-sm text-muted-foreground">
					{t("candidateJobSearch.results.foundCount", { count: searchResults.shortlist.length })}
				</p>
				<div className="flex items-center gap-3">
					<div className="flex items-center gap-1.5">
						<span className="text-xs text-muted-foreground mr-0.5">{t("advancedSearch.flexibility.label")}:</span>
						<div className="flex items-center gap-0.5 border border-border rounded p-0.5">
							{(['strict', 'normal', 'flexible'] as const).map(level => (
								<button
									key={level}
									onClick={() => setFlexibilityLevel(level)}
									className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
										flexibilityLevel === level
											? 'bg-blue-500 text-white'
											: 'text-muted-foreground hover:text-foreground'
									}`}
								>
									{t(`advancedSearch.flexibility.${level}`)}
								</button>
							))}
						</div>
					</div>
					<Button variant="outline" size="sm" onClick={runSearch}>
						<Search className="h-4 w-4 mr-2" />
						{t("candidateJobSearch.actions.rerun")}
					</Button>
				</div>
			</div>

			<div className="space-y-3">
				{searchResults.shortlist.map((job) => {
					const matchedSkills = job.skillComparison.filter(s => s.matched);
					const matchedExperiences = job.experienceComparison.filter(e => e.matched);
					const jobMeta = jobMetaById[job.jobId];
					const status = jobMeta?.status || job.jobStatus;
					const formattedDeadline = formatDeadline(jobMeta?.applicationDeadline);

				return (
					<Card key={job.jobId} className="border border-border">
						<CardContent className="p-4">
							<div className="flex items-start justify-between gap-4">
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 flex-wrap mb-1">
										<span
											className="font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
											onClick={() => navigate(`/jobs/${job.jobId}`)}
										>
											{job.jobTitle}
										</span>
										{status && (
											<Badge variant="outline" className={`text-[10px] h-5 ${statusBadgeClass(status)}`}>
												{status}
											</Badge>
										)}
										{job.combinedScore >= 85 && (
											<Badge variant="outline" className={`text-[10px] h-5 ${
												job.combinedScore >= 150
													? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700"
													: "bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700"
											}`}>
												{job.combinedScore >= 150 ? t("advancedSearch.results.highConfidence") : t("advancedSearch.results.strongConfidence")}
												</Badge>
											)}
										</div>

										{(jobMeta?.companyName || formattedDeadline) && (
											<div className="flex items-center gap-3 text-xs text-muted-foreground mb-2 flex-wrap">
												{jobMeta?.companyName && (
													<div className="flex items-center gap-1 min-w-0">
														<Building2 className="h-3.5 w-3.5 flex-shrink-0" />
														<span className="truncate">{jobMeta.companyName}</span>
													</div>
												)}
												{formattedDeadline && (
													<div className="flex items-center gap-1">
														<CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
														<span>Deadline: {formattedDeadline}</span>
													</div>
												)}
											</div>
										)}

										{jobSummaries[job.jobId] && (
										<p className="text-xs text-muted-foreground mb-2 line-clamp-2">{jobSummaries[job.jobId]}</p>
									)}
									{matchedSkills.length > 0 && (
											<div className="flex flex-wrap gap-1.5 mb-1">
												{matchedSkills.slice(0, 5).map((skill, idx) => (
													<Badge key={idx} variant="outline" className="text-[10px] h-5 border-blue-200 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300">
														{skill.candidateSkillName}
													</Badge>
												))}
												{matchedSkills.length > 5 && (
													<Badge variant="outline" className="text-[10px] h-5 text-muted-foreground">
														+{matchedSkills.length - 5}
													</Badge>
												)}
											</div>
										)}

										{matchedExperiences.length > 0 && (
											<div className="flex flex-wrap gap-1.5">
												{matchedExperiences.slice(0, 3).map((exp, idx) => (
													<Badge key={idx} variant="outline" className="text-[10px] h-5 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300">
														{exp.candidateExperienceName}
													</Badge>
												))}
												{matchedExperiences.length > 3 && (
													<Badge variant="outline" className="text-[10px] h-5 text-muted-foreground">
														+{matchedExperiences.length - 3}
													</Badge>
												)}
											</div>
										)}
									</div>

									<div className="flex flex-col items-end gap-2 flex-shrink-0">

										{job.isAlreadyMatched ? (
											<Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300 border-0 whitespace-nowrap">
												<CheckCircle2 className="h-3 w-3 mr-1" />
												{t("candidateJobSearch.results.alreadyMatched")}
											</Badge>
										) : (
											<Button
												size="sm"
												disabled={creatingMatchFor === job.jobId}
												onClick={() => handleCreateMatch(job)}
											>
												{creatingMatchFor === job.jobId ? (
													<Loader2 className="h-3.5 w-3.5 animate-spin" />
												) : (
													t("candidateJobSearch.actions.createMatch")
												)}
											</Button>
										)}
									</div>
								</div>
							</CardContent>
						</Card>
					);
				})}
			</div>
		</div>
	);
}
